
import { Component, Prop, Emit } from "vue-property-decorator";
import Vue from "vue";
import Draggable from "../utilities/draggable.vue";
import { NotifyNotificationRules, NotifySkipped, NotifyRuleReload, NotifySent } from "@/store/site-monitor/types";
import NotificationModalContent from "@/components/site-monitor/NotificationModalContent.vue";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import eventsApi from "@/services/api.event.service";
import { Getter } from 'vuex-class';
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";

@Component({
	components: {
		'draggable': Draggable,
		"notification-modal-content": NotificationModalContent
	}
})
export default class NotificationModalPopout extends Vue {
	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Prop({ type: Boolean}) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean = true;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	private isDraggable: boolean = true;
	private eventOutcome: EventOutcomeAndNote = {
		eventOutcomeId: null,
		eventOutcomeNote: null,
		valid: true
	};
	private selectedNotificationTemplate: NotificationTemplate | null = null;
	private modalSizeClass = 'draggable-size';

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Emit("notify-sent")
	private emitNotifySent(notifySent: NotifySent): NotifySent {
		return notifySent;
	}

	@Emit("notify-skipped")
	private emitNotifySkipped(notifySkipped: NotifySkipped): NotifySkipped {
		return notifySkipped;
	}

	@Emit("notify-rule-reload")
	private emitNotifyRuleReload(notifyRuleReload: NotifyRuleReload): NotifyRuleReload {
		return notifyRuleReload;
	}

	@Emit("cancel")
	private emitCancel(): boolean {
		return true;
	}

	@Emit("popout-closed")
	private async disablePopoutMode(): Promise<boolean> {
		await this.updateEventOutcome();
        return false;
    }

	private onFocusChanged(isFocused: boolean): void {
		this.isDraggable = !isFocused;
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	private get isNotificationModalLayoutEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "RearrangedLayout"]);
	}

	private async onEventOutcomeChange(value: EventOutcomeAndNote): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled) {
			this.eventOutcome = value;
		}
	}

	private async updateEventOutcome(): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled && this.selectedNotificationTemplate != null) {
			await eventsApi.preserveEventOutcome(this.eventId, 
				this.eventOutcome?.eventOutcomeId ?? null,
				this.eventOutcome?.eventOutcomeNote ?? null
			);
		}
	}

	private onTemplateChanged(newTemplate: NotificationTemplate): void {
		this.selectedNotificationTemplate = newTemplate;
	}

	private updateModalLayout(colCount: number): void {
		if (!this.isNotificationModalLayoutEnabled) {
			this.modalSizeClass = 'draggable-size';
			return;
		}

		if (colCount >= 2) {
			this.modalSizeClass = 'draggable-size_lg';
		} else {
			this.modalSizeClass = 'draggable-size';
		}												
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	private get notificationRuleTooltip(): string {
		if (!this.notifyRules) {
			return "";
		}

		return `Contacts have been assigned using the following Notification Rules: ${this.notifyRules.map(rule => rule.title).join(", ")}`;
	}
}
